import {
  Button,
  Drawer,
  Dropdown,
  Menu,
  MenuProps,
  Modal,
  Space,
  Avatar,
  Tag,
  Typography,
} from "antd";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectIdToken,
  selectUser,
  selectUserAccessLevel,
  selectUserRole,
} from "../access-control/accountSlice";
import styles from "./Navbar.module.css";
import { logout } from "../access-control/accountSlice";
import { createEnvHandoffServer } from "../access-control/accountAPI";
import {
  isProdPoolToken,
  KBM_URLS,
  ROAM_TARGET_ENVS,
  RoamEnv,
  STAGE,
} from "../../config";
import { IRouteConfig } from "../../interfaces/routeConfig.interface";
import useIsMobile from "../../hooks/useIsMobile";
import klearlyLogo from "../../logo.svg";

import {
  MenuUnfoldOutlined,
  CaretDownOutlined,
  UserOutlined,
  LogoutOutlined,
  LoadingOutlined,
  SettingOutlined,
  EllipsisOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { AccessLevel } from "../../interfaces/accessLevel.enum";

const { Text } = Typography;

interface IPropsNavbar {
  routes: IRouteConfig[];
}
export default function Navbar({ routes }: IPropsNavbar) {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const user = useAppSelector(selectUser);
  const role = useAppSelector(selectUserRole);
  const idToken = useAppSelector(selectIdToken);
  const canRoam = isProdPoolToken(idToken);

  const switchEnvironment = async (target: RoamEnv) => {
    if (target === STAGE) return;
    const run = async () => {
      const refreshToken = window.sessionStorage.getItem("refreshToken");
      if (!refreshToken) return;
      const { code } = await createEnvHandoffServer({
        targetEnv: target,
        refreshToken,
        idToken,
      });
      window.location.assign(`${KBM_URLS[target]}/?handoff=${code}`);
    };
    if (target === "prod") {
      Modal.confirm({
        title: "Switch to Production?",
        content: "You are about to enter the PRODUCTION environment.",
        okText: "Switch to Production",
        okButtonProps: { danger: true },
        onOk: run,
      });
      return;
    }
    await run();
  };

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };
  const userAccessLevel = useAppSelector(selectUserAccessLevel);
  // useEffect(() => {
  // }, [userAccessLevel]);

  const navigate = useNavigate();
  const location = useLocation();
  const logoutUser = async () => {
    setIsLoggingOut(true);
    try {
      await dispatch(logout());
      navigate("/");
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Build Ant Design menu items from the route table, filtered by the user's
  // access level. Groups become submenus; leaves become clickable items.
  const menuItems: MenuProps["items"] = useMemo(() => {
    const canAccess = (levels?: AccessLevel[]) =>
      Boolean(levels?.some((a) => userAccessLevel?.includes(a)));

    return routes
      .filter((r) => r.label && canAccess(r.accessLevel))
      .map((r) => {
        if (r.isGroup && r.children?.length) {
          const children = r.children
            .filter((c) => c.label && canAccess(c.accessLevel))
            .map((c) => ({ key: c.path, label: c.label }));
          if (children.length === 0) return null;
          return { key: r.path, label: r.label, children };
        }
        return { key: r.path, label: r.label };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [routes, userAccessLevel]);

  const onMenuClick: MenuProps["onClick"] = ({ key }) => {
    navigate(key);
    setOpen(false);
  };

  // Highlight the item matching the current path (child path for a submenu).
  const selectedKeys = useMemo(() => [location.pathname], [location.pathname]);
  return (
    <div className={styles.row}>
      {userAccessLevel && (
        <>
          <div className={styles.left_container}>
            <div className={styles.company_icon}>
              <img style={{ width: "20px" }} src={klearlyLogo} alt="Klearly" />
            </div>
            {user?.email && (
              <>
                {isMobile ? (
                  <div className="center">
                    <Button
                      type="primary"
                      onClick={showDrawer}
                      icon={<MenuUnfoldOutlined />}
                    ></Button>
                    <Drawer
                      title="Klearly"
                      placement="left"
                      onClose={onClose}
                      visible={open}
                      width="260px"
                      bodyStyle={{ padding: 0 }}
                    >
                      <Menu
                        mode="inline"
                        items={menuItems}
                        selectedKeys={selectedKeys}
                        onClick={onMenuClick}
                        style={{ borderInlineEnd: "none" }}
                      />
                    </Drawer>
                  </div>
                ) : (
                  <Menu
                    mode="horizontal"
                    items={menuItems}
                    selectedKeys={selectedKeys}
                    onClick={onMenuClick}
                    overflowedIndicator={<EllipsisOutlined />}
                    disabledOverflow={false}
                    className={styles.nav_menu}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      borderBottom: "none",
                      background: "transparent",
                    }}
                  />
                )}
              </>
            )}
          </div>
          <div className={styles.right_container}>
            {user?.email && (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "user-info",
                      label: (
                        <div
                          style={{
                            padding: "12px 16px",
                            marginBottom: "8px",
                            cursor: "default",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            <Avatar
                              size={48}
                              icon={<UserOutlined />}
                              style={{
                                backgroundColor: "#6B4EFF",
                                flexShrink: 0,
                              }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {user?.name && (
                                <Text
                                  strong
                                  style={{
                                    display: "block",
                                    fontSize: "16px",
                                    lineHeight: "22px",
                                    marginBottom: "2px",
                                  }}
                                >
                                  {user.name}
                                </Text>
                              )}
                              {user?.email && (
                                <Text
                                  type="secondary"
                                  style={{
                                    display: "block",
                                    fontSize: "14px",
                                    lineHeight: "18px",
                                    marginBottom: "2px",
                                  }}
                                >
                                  {user.email}
                                </Text>
                              )}
                              {role && (
                                <Text
                                  style={{
                                    display: "inline-block",
                                    fontSize: "12px",
                                    padding: "2px 8px",
                                    borderRadius: "10px",
                                    backgroundColor: "#f0f0ff",
                                    color: "#6B4EFF",
                                    marginTop: "4px",
                                    border: "1px solid #e6e6ff",
                                  }}
                                >
                                  {role}
                                </Text>
                              )}
                              {user?.partnerName && (
                                <Text
                                  style={{
                                    display: "block",
                                    fontSize: "13px",
                                    color: "#666",
                                    marginTop: "4px",
                                  }}
                                >
                                  {user.partnerName}
                                </Text>
                              )}
                            </div>
                          </div>
                        </div>
                      ),
                      disabled: true,
                    },
                    {
                      type: "divider",
                    },
                    ...(canRoam
                      ? [
                          {
                            key: "environment",
                            label: "Environment",
                            icon: <GlobalOutlined />,
                            children: ROAM_TARGET_ENVS.map((env) => ({
                              key: `env-${env}`,
                              label:
                                env === STAGE
                                  ? `${env.toUpperCase()} (current)`
                                  : env.toUpperCase(),
                              disabled: env === STAGE,
                              onClick: () => switchEnvironment(env),
                            })),
                          },
                          {
                            type: "divider" as const,
                          },
                        ]
                      : []),
                    {
                      key: "profile",
                      label: "Security",
                      icon: <SettingOutlined />,
                      onClick: () => navigate("/security"),
                    },
                    {
                      type: "divider",
                    },
                    {
                      key: "logout",
                      label: isLoggingOut ? "Signing Out..." : "Sign Out",
                      icon: isLoggingOut ? (
                        <LoadingOutlined />
                      ) : (
                        <LogoutOutlined />
                      ),
                      onClick: () => {
                        logoutUser();
                      },
                      disabled: isLoggingOut,
                      style: {
                        color: isLoggingOut ? "#999" : "#ff4d4f",
                      },
                    },
                  ],
                }}
                trigger={["click"]}
                placement="bottomRight"
                arrow={{ pointAtCenter: true }}
                disabled={isLoggingOut}
              >
                <Space
                  size="small"
                  style={{
                    cursor: isLoggingOut ? "not-allowed" : "pointer",
                    padding: "4px 8px",
                    borderRadius: "6px",
                    transition: "background-color 0.2s",
                    opacity: isLoggingOut ? 0.7 : 1,
                  }}
                  className="user-dropdown-trigger"
                >
                  <Avatar
                    size={32}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: "#6B4EFF" }}
                  />
                  <Text style={{ color: "inherit" }}>
                    {user?.name?.split(" ")[0] || "User"}
                  </Text>
                  <Tag
                    color={STAGE === "prod" ? "red" : "default"}
                    style={{ marginInlineEnd: 0 }}
                  >
                    {STAGE.toUpperCase()}
                  </Tag>
                  <CaretDownOutlined style={{ fontSize: "12px" }} />
                </Space>
              </Dropdown>
            )}
          </div>
        </>
      )}
    </div>
  );
}
